import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Paper,
    Typography,
    InputAdornment,
    IconButton,
    Chip,
    Stack,
    Pagination,
    Alert,
    Tooltip
} from '@mui/material';
import {
    Search as SearchIcon,
    FilterList as FilterIcon,
    Clear as ClearIcon,
    TrendingUp as TrendingIcon,
    History as HistoryIcon
} from '@mui/icons-material';
import AdvancedSearch from '../components/AdvancedSearch';
import SearchResults from '../components/SearchResults';
import ImageViewer from '../components/ImageViewer';
import DocumentViewer from '../components/DocumentViewer';
import authService from '../services/auth.service';

interface SearchFilters {
    query: string;
    fileTypes?: string[];
    categories?: string[];
    tags?: string[];
    dateFrom?: Date | null;
    dateTo?: Date | null;
    sizeMin?: number;
    sizeMax?: number;
    confidenceMin?: number;
    hasOCR?: boolean;
    department?: string;
    sharedWithMe?: boolean;
    myDocuments?: boolean;
}

const Search: React.FC = () => {
    const [query, setQuery] = useState('');
    const [filters, setFilters] = useState<SearchFilters>({ query: '' });
    const [results, setResults] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    const [viewingDocument, setViewingDocument] = useState<any>(null);
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [totalResults, setTotalResults] = useState(0);
    const [recentSearches, setRecentSearches] = useState<string[]>([]);
    const [popularTags, setPopularTags] = useState<string[]>([]);
    const [availableTags, setAvailableTags] = useState<string[]>([]);
    const [availableDepartments, setAvailableDepartments] = useState<string[]>([]);

    useEffect(() => {
        // Load recent searches from localStorage
        const saved = localStorage.getItem('recentSearches');
        if (saved) {
            setRecentSearches(JSON.parse(saved).slice(0, 5));
        }

        // Load popular tags and metadata
        fetchSearchMetadata();
    }, []);

    const fetchSearchMetadata = async () => {
        try {
            const api = authService.getApiInstance();
            const response = await api.get('/documents/search/metadata');
            setPopularTags(response.data.popularTags || []);
            setAvailableTags(response.data.allTags || []);
            setAvailableDepartments(response.data.departments || []);
        } catch (error) {
            console.error('Failed to fetch search metadata:', error);
        }
    };

    const handleSearch = async (searchFilters?: SearchFilters) => {
        const filtersToUse = searchFilters || { ...filters, query };
        
        if (!filtersToUse.query && Object.keys(filtersToUse).length === 1) {
            return;
        }

        setLoading(true);
        setFilters(filtersToUse);

        try {
            const api = authService.getApiInstance();
            const searchPayload: any = {
                page,
                limit: 20
            };
            
            // Only include query if it's not empty
            if (filtersToUse.query && filtersToUse.query.trim()) {
                searchPayload.query = filtersToUse.query;
            }
            
            // Only include filters if there are any
            const activeFilters = Object.entries(filtersToUse).reduce((acc, [key, value]) => {
                if (key !== 'query' && value !== undefined && value !== null) {
                    if (Array.isArray(value) && value.length === 0) return acc;
                    acc[key] = value;
                }
                return acc;
            }, {} as any);
            
            if (Object.keys(activeFilters).length > 0) {
                searchPayload.filters = activeFilters;
            }
            
            const response = await api.post('/documents/search', searchPayload);

            setResults(response.data.documents);
            setTotalPages(response.data.pagination.pages);
            setTotalResults(response.data.pagination.total);

            // Save to recent searches
            if (filtersToUse.query) {
                const newRecent = [filtersToUse.query, ...recentSearches.filter(s => s !== filtersToUse.query)].slice(0, 5);
                setRecentSearches(newRecent);
                localStorage.setItem('recentSearches', JSON.stringify(newRecent));
            }
        } catch (error) {
            console.error('Search failed:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleQuickSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        handleSearch();
    };

    const handleAdvancedSearch = (advancedFilters: SearchFilters) => {
        setFilters(advancedFilters);
        setQuery(advancedFilters.query);
        setPage(1);
        handleSearch(advancedFilters);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
        handleSearch();
    };

    const handleRecentSearch = (searchQuery: string) => {
        setQuery(searchQuery);
        setFilters({ ...filters, query: searchQuery });
        handleSearch({ ...filters, query: searchQuery });
    };

    const handleTagClick = (tag: string) => {
        const newFilters = {
            ...filters,
            tags: [...(filters.tags || []), tag]
        };
        setFilters(newFilters);
        handleSearch(newFilters);
    };

    const clearSearch = () => {
        setQuery('');
        setFilters({ query: '' });
        setResults([]);
        setPage(1);
        setTotalPages(1);
        setTotalResults(0);
    };

    const activeFiltersCount = Object.entries(filters).filter(([key, value]) => {
        if (key === 'query') return false;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'boolean') return value;
        return !!value;
    }).length;

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Typography variant="h4" gutterBottom>
                Document Search
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Search across all your documents using keywords, filters, and advanced options
            </Typography>

            {/* Search Bar */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <form onSubmit={handleQuickSearch}>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                        <div className="col-span-12 md:col-span-8">
                            <TextField
                                fullWidth
                                placeholder="Search documents by content, title, or tags..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: query && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={clearSearch}>
                                                <ClearIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </div>
                        <div className="col-span-12 md:col-span-2">
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => setAdvancedSearchOpen(true)}
                                startIcon={<FilterIcon />}
                                sx={{ height: '56px' }}
                            >
                                Advanced
                                {activeFiltersCount > 0 && (
                                    <Chip
                                        label={activeFiltersCount}
                                        size="small"
                                        color="primary"
                                        sx={{ ml: 1 }}
                                    />
                                )}
                            </Button>
                        </div>
                        <div className="col-span-12 md:col-span-2">
                            <Button
                                fullWidth
                                variant="contained"
                                type="submit"
                                disabled={loading}
                                startIcon={<SearchIcon />}
                                sx={{ height: '56px' }}
                            >
                                Search
                            </Button>
                        </div>
                    </div>
                </form>

                {/* Active Filters */}
                {activeFiltersCount > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {filters.fileTypes?.map((type) => (
                                <Chip
                                    key={type}
                                    label={`Type: ${type}`}
                                    size="small"
                                    onDelete={() => {
                                        const newTypes = filters.fileTypes?.filter(t => t !== type) || [];
                                        setFilters({ ...filters, fileTypes: newTypes });
                                    }}
                                />
                            ))}
                            {filters.categories?.map((cat) => (
                                <Chip
                                    key={cat}
                                    label={`Category: ${cat}`}
                                    size="small"
                                    onDelete={() => {
                                        const newCats = filters.categories?.filter(c => c !== cat) || [];
                                        setFilters({ ...filters, categories: newCats });
                                    }}
                                />
                            ))}
                            {filters.tags?.map((tag) => (
                                <Chip
                                    key={tag}
                                    label={`Tag: ${tag}`}
                                    size="small"
                                    onDelete={() => {
                                        const newTags = filters.tags?.filter(t => t !== tag) || [];
                                        setFilters({ ...filters, tags: newTags });
                                    }}
                                />
                            ))}
                            {filters.myDocuments && (
                                <Chip
                                    label="My Documents"
                                    size="small"
                                    onDelete={() => setFilters({ ...filters, myDocuments: false })}
                                />
                            )}
                            {filters.sharedWithMe && (
                                <Chip
                                    label="Shared with Me"
                                    size="small"
                                    onDelete={() => setFilters({ ...filters, sharedWithMe: false })}
                                />
                            )}
                        </Stack>
                    </Box>
                )}
            </Paper>

            {/* Quick Access */}
            {results.length === 0 && !loading && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Recent Searches */}
                    {recentSearches.length > 0 && (
                        <div>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <HistoryIcon />
                                    Recent Searches
                                </Typography>
                                <Stack spacing={1}>
                                    {recentSearches.map((search, index) => (
                                        <Chip
                                            key={index}
                                            label={search}
                                            onClick={() => handleRecentSearch(search)}
                                            onDelete={() => {
                                                const newRecent = recentSearches.filter((_, i) => i !== index);
                                                setRecentSearches(newRecent);
                                                localStorage.setItem('recentSearches', JSON.stringify(newRecent));
                                            }}
                                            sx={{ justifyContent: 'space-between' }}
                                        />
                                    ))}
                                </Stack>
                            </Paper>
                        </div>
                    )}

                    {/* Popular Tags */}
                    {popularTags.length > 0 && (
                        <div>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TrendingIcon />
                                    Popular Tags
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {popularTags.map((tag) => (
                                        <Chip
                                            key={tag}
                                            label={tag}
                                            onClick={() => handleTagClick(tag)}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            </Paper>
                        </div>
                    )}
                </div>
            )}

            {/* Search Results */}
            {(results.length > 0 || loading) && (
                <>
                    <SearchResults
                        results={results}
                        query={filters.query}
                        loading={loading}
                        totalResults={totalResults}
                        currentPage={page}
                        onViewDocument={(id) => {
                            // Find the document from results
                            const doc = results.find(r => r._id === id);
                            if (doc) {
                                setViewingDocument(doc);
                                setDocumentViewerOpen(true);
                            }
                        }}
                        onShareDocument={(id) => {
                            // Open share dialog
                            window.location.href = `/documents?share=${id}`;
                        }}
                        onRefresh={() => {
                            // Refresh search results with current filters
                            handleSearch(filters);
                        }}
                    />

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                            />
                        </Box>
                    )}
                </>
            )}

            {/* Advanced Search Dialog */}
            <AdvancedSearch
                open={advancedSearchOpen}
                onClose={() => setAdvancedSearchOpen(false)}
                onSearch={handleAdvancedSearch}
                initialFilters={filters}
                availableTags={availableTags}
                availableDepartments={availableDepartments}
            />

            {/* Image Viewer */}
            {viewingDocument && (
                <ImageViewer
                    open={imageViewerOpen}
                    onClose={() => {
                        setImageViewerOpen(false);
                        setViewingDocument(null);
                    }}
                    documentId={viewingDocument._id}
                    fileName={viewingDocument.fileSyncId?.fileName || ''}
                    fileSize={viewingDocument.fileSyncId?.fileSize || 0}
                    metadata={viewingDocument.metadata}
                />
            )}

            {/* Document Viewer */}
            {viewingDocument && (
                <DocumentViewer
                    open={documentViewerOpen}
                    onClose={() => {
                        setDocumentViewerOpen(false);
                        setViewingDocument(null);
                    }}
                    document={viewingDocument}
                />
            )}
        </Box>
    );
};

export default Search;